import { useState } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Sidebar } from '@/widgets/sidebar';
import { LocaleSwitcher } from '@/shared/ui/locale-switcher';
import { ROUTER_PATHS } from '@/app/routes';
import { useCurrentUser } from '@/features/auth/model/use-current-user';

/**
 * Main application layout wrapper
 * Includes sidebar navigation and provides consistent spacing for page content
 * Applied to all authenticated routes (excludes login, welcome, 404 pages)
 */
export function MainLayout() {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { userId, userName } = useCurrentUser();
	const initials =
		userName
			?.split(' ')
			.map((n) => n[0])
			.join('')
			.toUpperCase()
			.slice(0, 2) ?? '';

	return (
		<div className="flex h-screen bg-gray-50">
			{/* Backdrop — mobile only, closes sidebar on tap */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 bg-black/50 z-20 lg:hidden"
					onClick={() => setSidebarOpen(false)}
					aria-hidden="true"
				/>
			)}

			<Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

			<main className="flex-1 overflow-auto">
				{/* Mobile top bar */}
				<div className="lg:hidden flex items-center px-4 py-3 bg-brand-dark border-b border-gray-800">
					{/* Hamburger */}
					<button
						className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors"
						onClick={() => setSidebarOpen(true)}
						aria-label={formatMessage({ id: 'sidebar.openMenu' })}
					>
						<Menu className="w-5 h-5" />
					</button>

					{/* Right side: locale switcher + avatar */}
					<div className="ml-auto flex items-center gap-3">
						<LocaleSwitcher />
						<Link
							to={userId ? ROUTER_PATHS.userProfile.getLink(userId) : '#'}
							aria-label={userName ?? ''}
							className="hover:opacity-80 transition-opacity"
						>
							<div className="w-8 h-8 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center text-white text-sm font-medium">
								{initials}
							</div>
						</Link>
					</div>
				</div>

				<Outlet />
			</main>
		</div>
	);
}
